/**
 * 纪念日入场的极光（2026-10-02 改造需求：纪念日回访「极光升腾 → 专属寄语浮现」，ritual.ts）。
 * 一块盖满屏幕的面片，在星海场景里和星一起画（加法混合），参与辉光和色调映射；强度是 0 时不画。
 * 帘幕：横向用噪声扭出几道竖着的光带，下缘最亮、往上渐隐，颜色从青绿过渡到湖蓝、高处带一点玫瑰色；
 * uRise 从 0 到 1 时帘幕从屏幕下方升到画面中部。
 */
import * as THREE from 'three';

const VERT = /* glsl */ `
varying vec2 vUv;
void main() {
  vUv = position.xy * 0.5 + 0.5;
  gl_Position = vec4(position.xy, 0.999, 1.0);
}
`;

const FRAG = /* glsl */ `
uniform float uTime;
uniform float uK;
uniform float uRise;
uniform float uAspect;
varying vec2 vUv;

float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float noise(vec2 p) {
  vec2 i = floor(p), f = fract(p), u = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x), mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x), u.y);
}
float fbm(vec2 p) {
  float v = 0.0, a = 0.5;
  for (int k = 0; k < 4; k++) {
    v += a * noise(p);
    p = p * 2.03 + 17.0;
    a *= 0.5;
  }
  return v;
}

void main() {
  float x = vUv.x * uAspect;
  // 帘幕的下缘：随横向位置起伏，慢慢飘
  float base = mix(-0.25, 0.12, uRise) + 0.1 * (fbm(vec2(x * 0.9, uTime * 0.05)) - 0.5);
  float h = vUv.y - base;
  if (h < -0.03) discard;
  // 几幅帘幕：低频噪声决定哪里有光、哪里是缝；高频噪声是帘幕上一道道竖着的光丝
  float folds = smoothstep(0.3, 0.62, fbm(vec2(x * 2.4 + uTime * 0.03, 3.1)));
  float rays = 0.5 + 0.5 * noise(vec2(x * 46.0, uTime * 0.25 + h * 1.5));
  // 下缘一道亮边，往上暗下去，留一段长尾
  float up = max(h, 0.0);
  float edge = smoothstep(-0.03, 0.015, h) * (0.65 * exp(-up * 5.0) + 0.3 * exp(-up * 2.2));
  vec3 col = mix(vec3(0.15, 1.0, 0.55), vec3(0.15, 0.6, 1.0), smoothstep(0.02, 0.22, h));
  col = mix(col, vec3(1.0, 0.4, 0.7), smoothstep(0.18, 0.45, h) * 0.7);
  gl_FragColor = vec4(col * folds * rays * edge * uK * 0.7, 1.0);
  #include <colorspace_fragment>
}
`;

export class Aurora {
  readonly mesh: THREE.Mesh<THREE.PlaneGeometry, THREE.ShaderMaterial>;
  readonly uniforms = {
    uTime: { value: 0 },
    /** 整体强度 0–1；0 时不画 */
    uK: { value: 0 },
    /** 0–1：帘幕从屏幕下方升起来 */
    uRise: { value: 0 },
    uAspect: { value: 1 },
  };

  constructor() {
    const material = new THREE.ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: FRAG,
      uniforms: this.uniforms,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    this.mesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), material);
    // 顶点着色器直接给出屏幕坐标，包围球没有意义
    this.mesh.frustumCulled = false;
    this.mesh.visible = false;
  }

  /** 每帧调：强度是 0 就不画 */
  set(k: number, rise: number, time: number, aspect: number): void {
    const u = this.uniforms;
    u.uK.value = k;
    u.uRise.value = rise;
    u.uTime.value = time;
    u.uAspect.value = aspect;
    this.mesh.visible = k > 0.001;
  }
}
